import { Card, CardActions, CardContent, Grid, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useCallback } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import * as yup from 'yup';
import PmiTextField from 'shell/components/shared/text-field';
import { DateTime } from 'luxon';

export interface ConsumedEventForm {
  idConsumedEvent?: number;
  idPublishedEvent?: number;
  eventTypeCode?: string;
  eventTypeDestination?: string;
  subscriber?: string;
  progressive?: number;
  dateTimeConsummationStart?: DateTime | null;
  dateTimeConsummationEnd?: DateTime | null;
  numberAttempts?: number;
  state?: string;
  outcome?: string;
}

const validationSchema = yup.object().shape({
  idConsumedEvent: yup.number().required('Il campo ID evento consumato è obbligatorio'),
  idPublishedEvent: yup.string().required('Il campo ID evento pubblicato è obbligatorio'),
  eventTypeCode: yup.string().required('Il campo Tipo Evento è obbligatorio'),
  eventTypeDestination: yup.string().required('Il campo Tipo Destinazione è obbligatorio'),
  subscriber: yup.string().required('Il campo Sottoscrittore è obbligatorio'),
  progressive: yup.number().required('Il campo Progressivo è obbligatorio'),
  dateTimeConsummationStart: yup
    .string()
    .required('Il campo Data e ora inizio consumazione è obbligatorio'),
  dateTimeConsummationEnd: yup
    .string()
    .required('Il campo Data e ora fine consumazione è obbligatorio'),
  numberAttempts: yup.number().required('Il campo Num. Tentativi è obbligatorio'),
  state: yup.string().required('Il campo Stato è obbligatorio'),
  outcome: yup.string(),
});

interface FormConsumedEventProps {
  onOk: (form: ConsumedEventForm) => void;
  onCancel: () => void;
  title: string;
  disabled?: boolean;
  initialValues?: ConsumedEventForm;
  isEdit?: boolean;
}

const FormConsumedEvent = ({
  onOk,
  onCancel,
  title,
  disabled = true,
  initialValues,
  isEdit = false,
}: FormConsumedEventProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const formConsumedEvent = useFormik<ConsumedEventForm>({
    initialValues: initialValues || {
      idConsumedEvent: undefined,
      idPublishedEvent: undefined,
      eventTypeCode: '',
      eventTypeDestination: '',
      subscriber: '',
      progressive: undefined,
      dateTimeConsummationStart: null,
      dateTimeConsummationEnd: null,
      numberAttempts: undefined,
      state: '',
      outcome: '',
    },
    validationSchema: validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={formConsumedEvent.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2" color="secondary">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <PmiTextField
                  aria-label="ID evento consumato"
                  label="ID evento consumato"
                  id="idConsumedEvent"
                  name="idConsumedEvent"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formConsumedEvent.values.idConsumedEvent || ''}
                  disabled={disabled}
                  onChange={formConsumedEvent.handleChange}
                  error={
                    formConsumedEvent.touched.idConsumedEvent &&
                    Boolean(formConsumedEvent.errors.idConsumedEvent)
                  }
                  helperText={formConsumedEvent.errors.idConsumedEvent}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PmiTextField
                  aria-label="ID evento pubblicato"
                  label="ID evento pubblicato"
                  id="idPublishedEvent"
                  name="idPublishedEvent"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formConsumedEvent.values.idPublishedEvent || ''}
                  disabled={disabled}
                  onChange={formConsumedEvent.handleChange}
                  error={
                    formConsumedEvent.touched.idPublishedEvent &&
                    Boolean(formConsumedEvent.errors.idPublishedEvent)
                  }
                  helperText={formConsumedEvent.errors.idPublishedEvent}
                />
              </Grid>
              <Grid item xs={12} md={10}>
                <PmiTextField
                  aria-label="Dettaglio Tipo Evento"
                  label="Dettaglio Tipo Evento"
                  id="eventTypeCode"
                  name="eventTypeCode"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formConsumedEvent.values.eventTypeCode || ''}
                  disabled={disabled}
                  onChange={formConsumedEvent.handleChange}
                  error={
                    formConsumedEvent.touched.eventTypeCode &&
                    Boolean(formConsumedEvent.errors.eventTypeCode)
                  }
                  helperText={formConsumedEvent.errors.eventTypeCode}
                />
              </Grid>
              <Grid item xs={12} md={11}>
                <PmiTextField
                  aria-label="Tipo Destinazione"
                  label="Tipo Destinazione"
                  id="eventTypeDestination"
                  name="eventTypeDestination"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formConsumedEvent.values.eventTypeDestination || ''}
                  disabled={disabled || isEdit}
                  onChange={formConsumedEvent.handleChange}
                  error={
                    formConsumedEvent.touched.eventTypeDestination &&
                    Boolean(formConsumedEvent.errors.eventTypeDestination)
                  }
                  helperText={formConsumedEvent.errors.eventTypeDestination}
                />
              </Grid>
              <Grid item xs={12} md={11}>
                <PmiTextField
                  aria-label="Sottoscrittore"
                  label="Sottoscrittore"
                  id="subscriber"
                  name="subscriber"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formConsumedEvent.values.subscriber || ''}
                  disabled={disabled || isEdit}
                  onChange={formConsumedEvent.handleChange}
                  error={
                    formConsumedEvent.touched.subscriber &&
                    Boolean(formConsumedEvent.errors.subscriber)
                  }
                  helperText={formConsumedEvent.errors.subscriber}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <PmiTextField
                  aria-label="Progressivo"
                  label="Progressivo"
                  id="progressive"
                  name="progressive"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formConsumedEvent.values.progressive || ''}
                  disabled={disabled || isEdit}
                  onChange={formConsumedEvent.handleChange}
                  error={
                    formConsumedEvent.touched.progressive &&
                    Boolean(formConsumedEvent.errors.progressive)
                  }
                  helperText={formConsumedEvent.errors.progressive}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <PmiTextField
                  aria-label="Data e ora inizio consumazione"
                  label="Data e ora inizio consumazione"
                  id="dateTimeConsummationStart"
                  name="dateTimeConsummationStart"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={
                    formConsumedEvent?.values.dateTimeConsummationStart?.toFormat(
                      'dd/MM/yyyy HH:mm:ss'
                    ) || ''
                  }
                  disabled={disabled || isEdit}
                  onChange={formConsumedEvent.handleChange}
                  error={
                    formConsumedEvent.touched.dateTimeConsummationStart &&
                    Boolean(formConsumedEvent.errors.dateTimeConsummationStart)
                  }
                  helperText={formConsumedEvent.errors.dateTimeConsummationStart}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <PmiTextField
                  aria-label="Data e ora fine consumazione"
                  label="Data e ora fine consumazione"
                  id="dateTimeConsummationEnd"
                  name="dateTimeConsummationEnd"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={
                    formConsumedEvent.values.dateTimeConsummationEnd?.toFormat(
                      'dd/MM/yyyy HH:mm:ss'
                    ) || ''
                  }
                  disabled={disabled || isEdit}
                  onChange={formConsumedEvent.handleChange}
                  error={
                    formConsumedEvent.touched.dateTimeConsummationEnd &&
                    Boolean(formConsumedEvent.errors.dateTimeConsummationEnd)
                  }
                  helperText={formConsumedEvent.errors.dateTimeConsummationEnd}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <PmiTextField
                  aria-label="Num.tentativi"
                  label="Num.tentativi"
                  id="numberAttempts"
                  name="numberAttempts"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formConsumedEvent.values.numberAttempts || '0'}
                  disabled={disabled || isEdit}
                  onChange={formConsumedEvent.handleChange}
                  error={
                    formConsumedEvent.touched.numberAttempts &&
                    Boolean(formConsumedEvent.errors.numberAttempts)
                  }
                  helperText={formConsumedEvent.errors.numberAttempts}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <PmiTextField
                  aria-label="Stato"
                  label="Stato"
                  id="state"
                  name="state"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formConsumedEvent.values.state || ''}
                  disabled={disabled || isEdit}
                  onChange={formConsumedEvent.handleChange}
                  error={formConsumedEvent.touched.state && Boolean(formConsumedEvent.errors.state)}
                  helperText={formConsumedEvent.errors.state}
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <PmiTextField
                  aria-label="Esito"
                  label="Esito"
                  id="outcome"
                  name="outcome"
                  type="text"
                  multiline
                  rows={4}
                  variant="outlined"
                  fullWidth
                  value={formConsumedEvent.values.outcome || ''}
                  disabled={disabled || isEdit}
                  onChange={formConsumedEvent.handleChange}
                  error={
                    formConsumedEvent.touched.outcome && Boolean(formConsumedEvent.errors.outcome)
                  }
                  helperText={formConsumedEvent.errors.outcome}
                />
              </Grid>
            </Grid>
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel();
            }}
          >
            Indietro
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormConsumedEvent.displayName = 'Form Consumed Event component';

export default FormConsumedEvent;
